import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import IComps from '@/models/comp/IComps';
import ITotalCompData from '@/models/comp/ITotalCompData';
import DataDiscrepancyService from '@/services/2.6/DataDiscrepancyService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import { Actions, Getters, mlsStore, Mutations, store } from '@/store';
import { FieldNames } from '@/constants/FieldNames';
import ICompData, { newCompData } from '@/models/comp/ICompData';
import HostExchangeService from '@/services/2.6/HostExchangeService';
import utils from '@/utils/QSUtils';
import _ from 'lodash';
import IComp, { newComp } from '@/models/Comp';
import SalesHistoryManager from './SalesHistoryManager';
import LogUsageManager from '../LogUsageManager';
import IMLSPhotos from '@/models/mls/IMLSPhotos';
import IManualComp from '@/models/comp/IManualComp';
import { newProcessCompRequest } from '@/store/2.6/models/IProcessCompRequest';
import ICompAddress from '@/models/CompAddress';
import IManualComps from '@/models/comp/IManualComps';
import FormsManager from './FormsManager';
import IAddCompDataRequest, { newAddCompDataRequest } from '@/store/2.6/models/IAddCompDataRequest';
import ICompDatas from '@/models/comp/ICompDatas';
import IClearSourceDataRequest from '@/models/requests/IClearSourceDataRequest';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import OriginalValuesManager from './OriginalValuesManager';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import LocalStorageManager from '../LocalStorageManager';
import FeedDataUtils from '@/models/comp/FeedDataUtils';
import UserSettingsManager from '../UserSettingsManager';
import IFieldIdent from '@/models/forms/IFieldIdent';
import IFieldIdents from '@/models/forms/IFieldIdents';
import CompPrioritiesManager from './CompPrioritiesManager';
import CompTranslationsManager from './CompTranslationsManager';

/**
 * Manages the comps. Gets the comps from local storage or by comp id. Saves the
 * comps to the appropriate store.
 */
export default {
    /**
     * Get the comps and save to the store. Primary source is local storage.
     * Secondary source is using the comp id and going to the API service.
     */
    async fetchAndStore(): Promise<boolean> {
        let success = false;

        // Comps from local storage is preferred
        if (theLocalStorageUtils.hasKeyAndValue(LocalStorageKeys.almComps)) {
            const totalCompData = getFromLocalStorage();
            if (totalCompData) {
                await processCompsPhotos(totalCompData.totalComps);
                await saveToStore(totalCompData.totalComps);
                success = true;
                console.log(`CompsManager.fetchAndStore(), Using Comps from local storage, compId[${totalCompData.compsId}].`);
            }
        }

        if (!success) {
            // Fall back to using the comp id
            const compId = theLocalStorageUtils.get(LocalStorageKeys.almCompId) ?? '';

            const comps = await fetchByCompId(compId);
            if (comps) {
                await processCompsPhotos(comps);
                await saveToStore(comps);
                success = true;
                console.log(`CompsManager.fetchAndStore(), Using Comps from service, compId[${compId}].`);
            }
        }

        return success;
    },

    /**
     * Set the active comp from the given index. Subject is 0.
     * @param index
     */
    setActiveComp(index: number) {
        store.commit(Mutations.CompAddresses.SetSEColumnCollasped, true);
        store.commit(Mutations.CompAddresses.SetDBColumnCollasped, true);
        store.commit(Mutations.CompAddresses.ResetActiveComp);
        store.commit(Mutations.CompAddresses.UpdateActiveComp, index);

        // Check for mismatch address and if the choice is not to use closest (false, null indicates no choice made yet)
        const activeComp: IComp = store.getters[Getters.CompAddresses.ActiveComp];
        const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(activeComp.address);
        const misMatchAddress = LocalStorageManager.getMisMatchAddress(address1AndUnitNo);
        if (misMatchAddress && misMatchAddress.usingClosestAddress === false) {
            // If there is public record data (not already cleared) then clear the data
            if (activeComp.sourceDataPR && FeedDataUtils.hasValues(activeComp.sourceDataPR)) {
                // console.log(`CompsManager.setActiveComp, Mismatch indicates not using closest address and have PR source data, clearing...`);

                const sourceInitials = [SourceDataInitials.PublicRecords];
                this.clearSourceData(activeComp, sourceInitials);
            }
        }
    },

    /**
     * Add the given manual comps to the store. Scrubs the address and calls process comp on each (asynchronously).
     * @param manualComps
     */
    async addManualComps(manualComps: IManualComps): Promise<void> {
        const almCompId = theLocalStorageUtils.get(LocalStorageKeys.almCompId) ?? '';
        const hostExchangeService = HostExchangeService.getInstance();

        for (const manualComp of manualComps) {
            const compAddress = await scrubAddress(manualComp);
            if (!utils.isObjectEmpty(compAddress)) {
                const comp = newComp();
                comp.address = compAddress;

                // Send the new comp address to our host for adding before processing
                // Comp number is important here, give it the next in sequence
                const compCount: number = store.getters[Getters.CompAddresses.CompCount];
                comp.compNumber = compCount;
                await hostExchangeService.addCompAddress(almCompId, comp);

                // Now process the new comp
                const processCompRequest = newProcessCompRequest(comp);
                store.dispatch(Actions.CompAddresses.ProcessComp, processCompRequest);
            }
        }
    },

    /**
     * Back fill any missing fields for a comp in the store based on the given comp address and the
     * current form code. Affects the comp's source data (aka transfer data).
     * Also, assures 'DB_UNITNO', 'DB_CITY', 'DB_STATE' and 'DB_ZIPCODE' are included and uses the
     * given comp address data because a form may not include them and manually added comps won't
     * have them.
     * @param comp
     */
    async backFillMissingFields(compAddress: ICompAddress): Promise<void> {
        const storeComp: IComp | null = store.getters[Getters.CompAddresses.CompByAddress](compAddress);
        if (storeComp) {
            const request: IAddCompDataRequest = newAddCompDataRequest(compAddress);

            const formFields = await FormsManager.getFormFields();
            for (const fieldData of formFields) {
                if (storeComp.sourceData) {
                    const itemIdx = storeComp.sourceData.findIndex((cd) => cd.fieldName === fieldData.fieldName);
                    if (itemIdx >= 0) {
                        // Field exists, do not add another
                        continue;
                    }
                }

                const compData: ICompData = {
                    fieldId: fieldData.fieldId,
                    fieldName: fieldData.fieldName,
                    value: '',
                    sourceInitial: '',
                    include: '',
                    fieldRulesApplied: false,
                };

                request.compDatas.push(compData);
            }

            assureAddressCompDatas(compAddress, request.compDatas);

            if (request.compDatas.length > 0) {
                store.commit(Mutations.CompAddresses.AddCompData, request);
            }
        }
    },

    /**
     * Clear source data identified by the given sourceInitials
     * Transfer data will be updated accordingly and data preferences run as well.
     * @param comp
     * @param sourceInitials
     * @param runDataPriorities
     */
    async clearSourceData(comp: IComp, sourceInitials: SourceDataInitials[]): Promise<void> {
        const fieldIdents: IFieldIdents = [];

        if (comp.sourceData) {
            // Remove original values associated with transfer data from any of the given source initials
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);

            const formFields = await FormsManager.getFormFields();

            // Identify the transfer fields with data from any of the sources we're clearing
            for (const compData of comp.sourceData) {
                if (compData.sourceInitial === '' || compData.sourceInitial === SourceDataInitials.FormFiller || compData.include === '1') {
                    continue;
                }

                const foundIdx = sourceInitials.findIndex((si) => si === compData.sourceInitial);
                if (foundIdx >= 0) {
                    // CompData matches a source initial we're clearing
                    let fieldId = compData.fieldId ?? undefined;
                    if (!fieldId) {
                        // Comp Data is missing fieldId, see if we can find it in the form fields
                        const fieldData = formFields.find((fd) => fd.fieldName === compData.fieldName);
                        if (fieldData) {
                            fieldId = fieldData.fieldId;
                        }
                    }

                    const fieldIdent: IFieldIdent = {
                        fieldId: fieldId,
                        fieldName: compData.fieldName,
                    };
                    fieldIdents.push(fieldIdent);
                }
            }

            // Remove original values for the identified fields
            for (const fieldIdent of fieldIdents) {
                if (fieldIdent.fieldId) {
                    OriginalValuesManager.deleteValueByAddressAndFieldId(address1AndUnitNo, fieldIdent.fieldId);
                }
            }
        }

        // Clear the feed data(s) for the source initial(s)
        const request: IClearSourceDataRequest = {
            comp: comp,
            sourceInitials: sourceInitials,
        };
        store.commit(Mutations.CompAddresses.ClearCompSourceData, request);

        // Now run data priorities and then translations (field rules) on the identified fields
        if (comp.sourceData) {
            if (comp.compNumber === 0 && UserSettingsManager.isSubjectCascadeDisabled()) {
                // Skip if Subject transfer column is locked
                return;
            }

            for (const fieldIdent of fieldIdents) {
                if (fieldIdent.fieldId) {
                    const fieldData = await FormsManager.getFieldDataByName(fieldIdent.fieldName);
                    const fieldInfo = `${fieldData?.fieldId},${fieldData?.fieldName},${fieldData?.friendlyFieldName}`;
                    console.log(`CompsManager.clearSourceData(), running priorities on field[${fieldInfo}]`);

                    await CompPrioritiesManager.runOnField(comp, fieldIdent.fieldId);
                    await CompTranslationsManager.runOnField(comp, fieldIdent.fieldId);
                }
            }
        }
    },

    /**
     * Delete a comp identified by the given tab index.
     * @param tabIndex
     */
    async deleteComp(tabIndex: number): Promise<void> {
        const comps = store.getters[Getters.CompAddresses.Comps] as IComps;
        const comp = comps[tabIndex];

        await SalesHistoryManager.removeSelections(comp.address);
        LogUsageManager.deletedCompTabs(comp.address, tabIndex);
        await store.dispatch(Actions.CompAddresses.RemoveComp, tabIndex);
    },

    getActiveCompPhotoCount() {
        // let count = 0;
        // const comp = store.getters[Getters.CompAddresses.ActiveComp] as IComp;

        // // Get Comp Photos from each source
        // for (const [key, item] of Object.entries(comp)) {
        //     if (key === 'sourceDataDB' || key === 'sourceDataPR' || key === 'sourceDataSE') {
        //         const obj = _.cloneDeep(item.sourceData) as any;
        //         if (obj?.COMP_PHOTO) {
        //             count = count + 1;
        //         }
        //     } else if (key === 'sourceDataMLS') {
        //         count = count + mlsStore.MlsPropertyAdditionalPhotoCountByAddress(comp.address);
        //     }
        // }

        // // Get subComps Photos from each source
        // if (comp?.subCompData) {
        //     for (const initials in comp.subCompData) {
        //         if (utils.isKeyInObject(comp.subCompData, initials.toLowerCase())) {
        //             const subComps = comp.subCompData[initials.toLowerCase()];
        //             if (subComps.length > 0) {
        //                 for (const idx in subComps) {
        //                     const obj = _.cloneDeep(subComps[idx]) as any;
        //                     if (obj?.COMP_PHOTO) {
        //                         count = count + 1;
        //                     }
        //                 }
        //             }
        //         }
        //     }
        // }

        const photos = this.getActiveCompPhotos();
        return photos.length || 0;
    },

    getActiveCompPhotos() {
        const photos: { sourceInitial: string; full: any; thumbnail: any; guid: string }[] = [];
        const comp = store.getters[Getters.CompAddresses.ActiveComp] as IComp;

        // Get Comp Photos from each source
        for (const [key, item] of Object.entries(comp)) {
            if (key === 'sourceDataDB' || key === 'sourceDataPR' || key === 'sourceDataSE') {
                const sourceInitial = key.slice(-2);
                const obj = _.cloneDeep(item.sourceData) as any;
                if (obj?.COMP_PHOTO) {
                    const initial = sourceInitial.toLowerCase();
                    const photo = {
                        sourceInitial: sourceInitial.toLowerCase(),
                        full: obj?.COMP_PHOTO,
                        thumbnail: obj?.COMP_PHOTO,
                        guid: utils.uuid(),
                    };

                    if (!checkForDupPhotos(photos, initial, obj?.COMP_PHOTO)) {
                        photos.push(photo);
                    }
                }
            } else if (key === 'sourceDataMLS') {
                const pics = mlsStore.MlsPropertyAdditionalPhotosByAddress(comp.address) as unknown as IMLSPhotos;
                Object.keys(pics).forEach((k, i) => {
                    const data = Object.values(pics)[i];
                    const photo = {
                        sourceInitial: 'mls',
                        full: data.full,
                        thumbnail: data.thumbnail,
                        guid: utils.uuid(),
                    };

                    if (!checkForDupPhotos(photos, 'mls', data.full)) {
                        photos.push(photo);
                    }
                });
            }
        }

        // Get subComps Photos from each source
        if (comp?.subCompData) {
            for (const initials in comp.subCompData) {
                if (utils.isKeyInObject(comp.subCompData, initials.toLowerCase())) {
                    const subComps = comp.subCompData[initials.toLowerCase()];
                    if (subComps.length > 0) {
                        for (const idx in subComps) {
                            const obj = _.cloneDeep(subComps[idx]) as any;
                            if (obj?.COMP_PHOTO) {
                                const initial = initials.toLowerCase();
                                const photo = {
                                    sourceInitial: initial,
                                    full: obj?.COMP_PHOTO,
                                    thumbnail: obj?.COMP_PHOTO,
                                    guid: utils.uuid(),
                                };

                                if (!checkForDupPhotos(photos, initial, obj?.COMP_PHOTO)) {
                                    photos.push(photo);
                                }
                            }
                        }
                    }
                }
            }
        }

        return photos;
    },

    compsLoading(): boolean {
        const comps = store.getters[Getters.CompAddresses.Comps] as IComps;
        return comps.some((obj) => obj.isLoading === true);
    },
};

function checkForDupPhotos(photos: any, initials: string, path: string) {
    const idx = photos.findIndex((f: any) => f.sourceInitial === initials && f.full === path);
    if (idx === -1) {
        return false;
    }

    return true;
}

function getFromLocalStorage(): ITotalCompData | null {
    const compsString = localStorage.getItem(LocalStorageKeys.almComps);
    if (compsString) {
        let compsObject: any | null = null;
        try {
            compsObject = JSON.parse(compsString);
        } catch (error) {
            console.log(`CompsManager.getFromLocalStorage(), Parse error[${error}]`);

            try {
                const compsFixed = handleKnownIssues(compsString);
                compsObject = JSON.parse(compsFixed);
            } catch (error) {
                console.log(`CompsManager.getFromLocalStorage(), Error after fixing known issues[${error}]`);
            }
        }

        if (compsObject) {
            const totalCompData = convertKeysToCamelCase(compsObject);
            if (totalCompData) {
                prepareCompsFromTotal(totalCompData);
            }
            return totalCompData;
        }
    }

    return null;
}

/**
 * Called when there is some error in parsing the comps string.
 * This method handles calling all the known issues (so far).
 * @param compsString
 * @returns a string with any fixes made
 */
function handleKnownIssues(compsString: string): string {
    // We've experienced issues with double backslashes that need replacing
    const compsFixed = handleBackslashes(compsString);

    //
    // Any other fix methods go here, possibly in some specific order
    //

    return compsFixed;
}

/**
 * Checks the string for double backslashes and replaces them with quadruple backslashes.
 * It's an escape sequence thing b/c of both the Javascript interpreter and it's a JSON string.
 * @param compsString
 * @returns the fixed string if there are replacements made, the given string if not
 */
function handleBackslashes(compsString: string): string {
    try {
        const regExpArray = /(\\)/g.exec(compsString);
        if (regExpArray) {
            // There are some backslashes that need fixing
            console.log(`CompsManager.handleBackslashes(), Occurrences[${JSON.stringify(regExpArray)}]`);
            const compsFixed = compsString.replaceAll(/(\\)/g, '\\\\');
            return compsFixed;
        }
    } catch (error) {
        console.log(`CompsManager.handleBackslashes(), Error[${error}]`);
    }

    return compsString;
}

function convertKeysToCamelCase(objectData: any): ITotalCompData {
    // Get the object as a string
    const objectString = JSON.stringify(objectData);

    // Parse the string using a reviver to make the keys camelCase
    const totalCompData = JSON.parse(objectString, function (key, value) {
        if (value && typeof value === 'object' && !Array.isArray(value))
            for (const k in value) {
                if (/^[A-Z]/.test(k) && Object.hasOwnProperty.call(value, k)) {
                    value[k.charAt(0).toLowerCase() + k.substring(1)] = value[k];
                    delete value[k];
                }
            }
        return value;
    }) as ITotalCompData;

    return totalCompData;
}

/**
 * Preparation as done by the QuickSource API when requesting "CompAddressAndFormFillerData".
 * @param totalCompData
 */
function prepareCompsFromTotal(totalCompData: ITotalCompData): boolean {
    try {
        for (const comp of totalCompData.totalComps) {
            if (comp.compKey) {
                comp.propertyPhoto = comp.compKey;
            } else if (comp.propertyPhoto) {
                comp.compKey = comp.propertyPhoto;
            }

            if (comp.sourceData) {
                const filtered = comp.sourceData.filter((sd) => (sd.include && sd.include === '1') || sd.value);
                comp.sourceData = filtered;
            }
        }
    } catch (error) {
        console.log(`CompsManager.prepareCompsFromTotal(), Failed to parse Total comp data[${error}]`);
        return false;
    }
    return true;
}

async function fetchByCompId(compId: string): Promise<IComps> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();

    const service = new DataDiscrepancyService(webEnvConfig);
    const comps = await service.getCompAddresses(compId);

    return comps;
}

async function scrubAddress(manualComp: IManualComp): Promise<ICompAddress> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();

    const service = new DataDiscrepancyService(webEnvConfig);
    const compAddress = await service.scrubAddress(manualComp);

    return compAddress;
}

/**
 * Use the given comps compKeys to get the Base64 Image data for the COMP_PHOTO.
 * Create a comp data for the COMP_PHOTO if one doesn't exist.
 * @param comps
 */
async function processCompsPhotos(comps: IComps) {
    if (comps) {
        const service = HostExchangeService.getInstance();

        for (const comp of comps) {
            if (comp.sourceData) {
                if (comp.compKey) {
                    const compPhoto = await service.getBase64ForImage(comp.compKey);

                    const compPhotoIdx = comp.sourceData.findIndex((sd) => sd.fieldName === FieldNames.CompPhoto);
                    if (compPhotoIdx < 0) {
                        const compData = newCompData();
                        compData.fieldName = FieldNames.CompPhoto;
                        compData.value = compPhoto;

                        comp.sourceData.push(compData);
                    } else {
                        const compData = comp.sourceData[compPhotoIdx];
                        compData.value = compPhoto;
                    }
                }
            }
        }
    }
}

/**
 * Call the store's process comps action on the given comps.
 * @param comps
 */
async function saveToStore(comps: IComps): Promise<void> {
    try {
        await store.dispatch(Actions.CompAddresses.ProcessComps, comps);
    } catch (error) {
        console.log(`CompsManager.saveToStore(), Error processing comps[${error}]`);
    }
}

/**
 * Assure the given comp datas include a comp data for the extra address fields -
 * 'DB_UNITNO', 'DB_CITY', 'DB_STATE' and 'DB_ZIPCODE'. Uses the given comp address to
 * populate either existing comp data values or those created within.
 *
 * @param compAddress
 * @param compDatas
 */
function assureAddressCompDatas(compAddress: ICompAddress, compDatas: ICompDatas) {
    const fieldNames = [FieldNames.UnitNo, FieldNames.City, FieldNames.State, FieldNames.ZipCode];
    const fieldValues = [compAddress.unitNumber ?? '', compAddress.city ?? '', compAddress.state ?? '', compAddress.zipCode ?? ''];

    for (const [idx, fieldName] of fieldNames.entries()) {
        let compData = compDatas.find((cd) => cd.fieldName === fieldName);
        if (compData) {
            compData.value = fieldValues[idx];
        } else {
            compData = {
                //fieldId: ???,
                fieldName: fieldName,
                value: fieldValues[idx],
                sourceInitial: '',
                include: '',
                fieldRulesApplied: false,
            };
            compDatas.push(compData);
        }
    }
}
